import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { catchError, of, switchMap } from 'rxjs';

import { AssignmentService } from '../../core/assignment/assignment.service';
import { AssignmentKind, AssignmentState } from '../../core/assignment/assignment.types';
import { ModalComponent } from '../modal/modal';
import { placeRefusal } from '../modal/modal-refusal';
import { readError } from '../../core/http/read-error';
import { AssignmentHistoryDialogComponent } from './history-dialog';

export interface MoveDialogData {
  readonly kind: AssignmentKind;
  readonly shortId: string;
  readonly state: AssignmentState;
}

/** The value the select uses for "nobody" — a real person's id is a UUID. */
const NOBODY = 'NOBODY';

/**
 * Move an application to someone else, or back to nobody. Closes with the new
 * state only once the server has recorded the move; a refusal (a stale view,
 * a missing reason) shows inside the dialog with the input kept (FLOW-378).
 */
@Component({
  selector: 'app-assignment-move-dialog',
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ModalComponent,
    ReactiveFormsModule,
  ],
  template: `
    <app-modal
      heading="Who should deal with this application?"
      submitLabel="Move"
      [formGroup]="form"
      [pending]="pending()"
      [error]="error()"
      (submitted)="save()"
    >
      @if (data.state.assignee_is_me) {
        <div class="move-dialog__mine">
          <span>You're dealing with this.</span>
          <button
            mat-stroked-button
            type="button"
            [disabled]="pending()"
            (click)="notMe()"
            data-testid="assignment-not-me"
          >
            Not me
          </button>
        </div>
      }
      <div class="modal-fields">
        <mat-form-field appearance="outline">
          <mat-label>Dealt with by</mat-label>
          <mat-select formControlName="to" data-testid="assignment-move-to">
            @for (p of people(); track p.id) {
              <mat-option [value]="p.id">{{ p.name }}</mat-option>
            }
            <mat-option [value]="nobody">Nobody</mat-option>
          </mat-select>
          @if (form.controls.to.hasError('serverError')) {
            <mat-error>{{ form.controls.to.getError('serverError') }}</mat-error>
          } @else {
            <mat-error>Choose who should deal with it.</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Reason</mat-label>
          <textarea
            matInput
            rows="3"
            formControlName="reason"
            data-testid="assignment-move-reason"
          ></textarea>
          @if (reasonRequired()) {
            <mat-hint>Required — {{ holderName() }} is dealing with it now.</mat-hint>
          }
          <mat-error>{{ form.controls.reason.getError('serverError') }}</mat-error>
        </mat-form-field>
      </div>
      @if (data.state.history.length) {
        <span modalFooterStart>
          <button
            mat-button
            type="button"
            (click)="openHistory()"
            data-testid="assignment-history-open"
          >
            See past moves
          </button>
        </span>
      }
    </app-modal>
  `,
  styles: `
    .move-dialog__mine {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-2);
      margin-bottom: var(--space-4);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentMoveDialogComponent {
  protected readonly data = inject<MoveDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<AssignmentMoveDialogComponent, AssignmentState>>(MatDialogRef);
  private readonly service = inject(AssignmentService);
  private readonly dialog = inject(MatDialog);

  protected readonly nobody = NOBODY;
  protected readonly people = toSignal(this.service.assignableUsers(this.data.kind), {
    initialValue: [],
  });
  protected readonly form = inject(FormBuilder).nonNullable.group({
    to: ['', Validators.required],
    reason: [''],
  });
  protected readonly pending = signal(false);
  protected readonly error = signal<string | null>(null);

  private readonly holder = this.data.state.assignee;
  protected readonly holderName = computed(() => this.holder?.name ?? '');
  /** Moving it away from a colleague needs a reason; your own does not. */
  protected readonly reasonRequired = computed(
    () => !!this.holder && !this.data.state.assignee_is_me,
  );

  protected openHistory(): void {
    this.dialog.open(AssignmentHistoryDialogComponent, { data: this.data.state.history });
  }

  /** Back to nobody, and the viewer is not asked to take it again on this record. */
  protected notMe(): void {
    if (this.pending()) return;
    const { kind, shortId } = this.data;
    this.pending.set(true);
    this.error.set(null);
    this.service
      .move(kind, shortId, { to_user_id: null, expected_from_user_id: this.holder?.id ?? null })
      .pipe(
        switchMap((moved) =>
          // The move stands even if this fails; they are just asked again next time.
          this.service.notNow(kind, shortId).pipe(catchError(() => of(moved))),
        ),
      )
      .subscribe({
        next: (state) => this.dialogRef.close(state),
        error: (err: unknown) => {
          this.pending.set(false);
          this.error.set(readError(err));
        },
      });
  }

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { to, reason } = this.form.getRawValue();
    this.pending.set(true);
    this.error.set(null);
    this.service
      .move(this.data.kind, this.data.shortId, {
        to_user_id: to === NOBODY ? null : to,
        expected_from_user_id: this.holder?.id ?? null,
        reason,
      })
      .subscribe({
        next: (state) => this.dialogRef.close(state),
        error: (err: unknown) => {
          this.pending.set(false);
          this.error.set(placeRefusal(this.form, err, readError(err), { to_user_id: 'to' }));
        },
      });
  }
}
